import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function MyAds({ userId, equipmentModels, requestsType }) {
    const [myRequests, setMyRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all'); // all, active, sold

    useEffect(() => {
        fetchMyAds();
    }, [userId]);

    const fetchMyAds = async () => {
        try {
            const response = await fetch(API_URL + '/requests');
            const data = await response.json();
            // ✅ Фильтруем: только СВОИ и только "Сдаю технику" (type_id = 2)
            // ✅ Оба типа: 1 = «Ищу технику», 2 = «Сдаю технику»
            const filtered = data.filter(req => 
                req.user_id === parseInt(userId)
            );
            setMyRequests(filtered);
        } catch (error) {
            console.error('Ошибка загрузки объявлений:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Удалить объявление?')) return;
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(API_URL + '/requests/' + id, {
                method: 'DELETE',
                headers: { 'Authorization': 'Bearer ' + token }
            });
            if (response.ok) {
                alert('✅ Объявление удалено');
                fetchMyAds();
            } else {
                const err = await response.json();
                alert(err.message || 'Ошибка удаления');
            }
        } catch (error) {
            console.error('Ошибка:', error);
            alert('Ошибка удаления');
        }
    };

    const formatDate = (date) => {
        if (!date) return '—';
        return new Date(date).toLocaleDateString('ru-RU', {
            day: '2-digit', month: '2-digit', year: 'numeric'
        });
    };

    const getEquipmentName = (id) => equipmentModels?.[id] || 'Не указана';
    const getTypeName = (id) => requestsType?.[id] || '—';

    const filteredRequests = myRequests.filter(req => {
        if (filter === 'active') return req.status === 'new';
        return true;
    });

    if (loading) {
        return <div className="text-center py-12 text-slate-500">Загрузка объявлений...</div>;
    }

    return (
        <div className="max-w-7xl mx-auto">
            {/* Заголовок */}
            <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl md:text-3xl font-black text-slate-900 mb-1">
                        Мои заявки
                    </h1>
                    <p className="text-sm text-slate-500">
                        Всего: <strong className="text-gold-500">{myRequests.length}</strong> заявок
                    </p>
                </div>
                
                <div className="flex gap-2">
                    <button
                        onClick={() => setFilter('all')}
                        className={
                            'px-4 py-2 rounded-lg text-sm font-medium transition ' +
                            (filter === 'all' 
                                ? 'bg-dark-900 text-white' 
                                : 'bg-white text-slate-600 border border-slate-200 hover:border-gold-400')
                        }
                    >
                        Все
                    </button>
                    <button
                        onClick={() => setFilter('active')}
                        className={
                            'px-4 py-2 rounded-lg text-sm font-medium transition ' +
                            (filter === 'active' 
                                ? 'bg-dark-900 text-white' 
                                : 'bg-white text-slate-600 border border-slate-200 hover:border-gold-400')
                        }
                    >
                        Активные
                    </button>
                </div>
            </div>

            {/* Пусто */}
            {filteredRequests.length === 0 ? (
                <div className="bg-white rounded-2xl border-2 border-dashed border-slate-300 p-12 text-center">
                    <div className="text-6xl mb-4">📢</div>
                    <h3 className="text-xl font-bold text-slate-800 mb-2">
                        {filter === 'active' ? 'Нет активных заявок' : 'У вас пока нет заявок'}
                    </h3>
                    <p className="text-slate-500 mb-6">
                        Создайте первую заявку — «Ищу технику» или «Сдаю технику»
                    </p>
                </div>
            ) : (
                /* ✅ Две секции: Ищу технику + Сдаю технику */
                (() => {
                    const searching = filteredRequests.filter(r => r.type_id === 1);
                    const offering = filteredRequests.filter(r => r.type_id === 2);
                    const renderCard = (request) => {
                        const isNew = request.status === 'new';
                        const pricePerHour = parseFloat(request.price_per_hour) || 0;
                        
                        return (
                            <div 
                                key={request.id}
                                className="bg-white rounded-2xl border-2 border-slate-200 hover:border-gold-400 hover:shadow-lg transition-all overflow-hidden flex flex-col"
                            >
                                {/* Фото */}
                                <div className="relative h-44 bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center overflow-hidden">
                                    {request.photo_url ? (
                                        <img 
                                            src={request.photo_url} 
                                            alt={getEquipmentName(request.equipment_id)}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div className="text-6xl opacity-40">🚜</div>
                                    )}
                                    
                                    {/* Бейдж статуса */}
                                    <div className="absolute top-3 left-3">
                                        <span className={
                                            'px-3 py-1 rounded-full text-xs font-bold ' +
                                            (isNew 
                                                ? 'bg-emerald-500 text-white' 
                                                : 'bg-slate-500 text-white')
                                        }>
                                            {isNew ? '🟢 Активно' : '⚪ ' + request.status}
                                        </span>
                                    </div>

                                    {/* ID */}
                                    <div className="absolute top-3 right-3">
                                        <span className="bg-black/60 backdrop-blur-sm text-white text-xs font-bold px-2 py-1 rounded">
                                            #{request.id}
                                        </span>
                                    </div>
                                </div>

                                {/* Контент */}
                                <div className="p-4 flex-1 flex flex-col">
                                    <h3 className="text-base font-bold text-slate-900 mb-2 line-clamp-2">
                                        {getEquipmentName(request.equipment_id)}
                                    </h3>
                                    
                                    <div className="flex items-center gap-2 mb-3">
                                        <span className="text-xs bg-gold-100 text-gold-700 px-2 py-0.5 rounded font-medium">
                                            {getTypeName(request.type_id)}
                                        </span>
                                    </div>

                                    <div className="space-y-2 mb-4 flex-1">
                                        <div className="flex items-center gap-2 text-sm text-slate-600">
                                            <span className="text-slate-400">📍</span>
                                            <span className="line-clamp-1">{request.city}, {request.address}</span>
                                        </div>
                                        
                                        <div className="flex items-center gap-2 text-sm text-slate-600">
                                            <span className="text-slate-400">📅</span>
                                            <span>{formatDate(request.date_requested)}</span>
                                        </div>

                                        {request.details && (
                                            <div className="flex items-start gap-2 text-sm text-slate-600">
                                                <span className="text-slate-400">💬</span>
                                                <span className="line-clamp-2">{request.details}</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Цена */}
                                    <div className="bg-gold-50 border border-gold-200 rounded-xl p-3 mb-4">
                                        <div className="text-xs text-gold-700 font-medium mb-0.5">Цена аренды</div>
                                        <div className="text-xl font-black text-gold-600">
                                            {pricePerHour.toLocaleString('ru-RU')} <span className="text-sm font-medium">₽/час</span>
                                        </div>
                                    </div>

                                    {/* Кнопки */}
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => alert('Функция в разработке')}
                                            className="flex-1 py-2 bg-dark-900 hover:bg-dark-800 text-white rounded-lg text-sm font-medium transition"
                                        >
                                            ✏️ Редактировать
                                        </button>
                                        <button
                                            onClick={() => handleDelete(request.id)}
                                            className="px-3 py-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg text-sm font-medium transition border border-red-200"
                                            title="Удалить"
                                        >
                                            🗑️
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    };
                    return (
                        <>
                            {searching.length > 0 && (
                                <div className="mb-8">
                                    <h2 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2">
                                        🔍 Ищу технику <span className="text-sm text-slate-500 font-normal">({searching.length})</span>
                                    </h2>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                                        {searching.map(renderCard)}
                                    </div>
                                </div>
                            )}
                            {offering.length > 0 && (
                                <div>
                                    <h2 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2">
                                        🚜 Сдаю технику <span className="text-sm text-slate-500 font-normal">({offering.length})</span>
                                    </h2>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                                        {offering.map(renderCard)}
                                    </div>
                                </div>
                            )}
                        </>
                    );
                })()
            )}
        </div>
    );
}

export default MyAds;
